import React, {
  useCallback, useEffect, useState, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { CSVLink } from 'react-csv';
import {
  Button, Dimmer, Loader, Message,
} from 'semantic-ui-react';
import { graphql } from 'react-apollo';
import LocationContactList from '../views/LocationContact/LocationContactList';
import withPagination from './WithPagination';
import { setPage } from '../data/page/actions';
import apmsLocationContactsQuery from '../graphql/apmsLocationContactsQuery';
import LoadingDataEnhancer from './LoadingData';
import { exportFileName, auctionContactCSV, auctionContactHeader } from '../views/AuctionContacts/AuctionContactsExporter';
import client from '../apollo';
import createOrUpdateLocationContactMutation from '../graphql/updateLocationContactQuery';
import AuctionContactForm, {
  AuctionLocationContactAction,
} from '../views/AuctionContacts/AuctionContactForm/AuctionContactForm';
import { messageSuccess } from './AuctionContacts';
import useGraphQLQuery from '../hooks/useGraphQLQuery';
import { hasRole } from '../utilities/user';
import { LocationContactInfoAction } from '../views/LocationContact/LocationContactListRow';

const AllAuctionContact = props => {
  const {
    user,
    data,
    createOrUpdateLocationContact,
    setPage: setPageAction,
  } = props;
  const isAuctionLocationEditor = hasRole('auction_location_editor', 'roles')(user);
  const [locationContactData, setLocationContactData] = useState([]);

  const [csvData, setCsvData] = useState([]);
  const [csvReady, setCsvReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const csvLinkRef = useRef();

  const [selectedContact, setSelectedContact] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [modalError, setModalError] = useState('');
  const [successMessage, setSuccessMessage] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [flashMessage, setFlashMessage] = useState('');
  const [isNegativeFlashMsg, setIsNegativeFlashMsg] = useState(false);

  const {
    loading: contactLoading,
  } = useGraphQLQuery({
    query: apmsLocationContactsQuery,
  });

  useEffect(() => {
    setLocationContactData(data.apmsLocationContactsList);
  }, [data]);

  const onSelectLocationContact = (contact, action) => {
    switch (action) {
      case LocationContactInfoAction.EDIT:
        setSelectedContact(contact);
        setLocationId(contact.locationId);
        setShowContactModal(true);
        break;
      default:
        break;
    }
  };

  const handleExportCSV = async() => {
    if (isExporting) return; // Prevent multiple clicks
    setIsExporting(true);
    try {
      const result = await client.query({
        query: apmsLocationContactsQuery,
        variables: { isPaginationActive: false },
        fetchPolicy: 'network-only',
      });
      const list = result.data?.apmsLocationContactsList || [];
      setCsvData(auctionContactCSV(list));
      setCsvReady(true);

      // wait for state updated before trigger download
      setTimeout(() => {
        csvLinkRef.current.link.click();
        setCsvReady(false);
        setIsExporting(false);
      }, 0);
    } catch (err) {
      setIsExporting(false);
    }
  };

  const onFailedSubmit = useCallback((error, message) => {
    setIsLoading(false);
    const errorMsg = message !== undefined
      ? message
      : Array.isArray(error)
        ? error.join(', ')
        : error.message;
    setModalError(errorMsg);
  }, []);

  const getContactActionInput = useCallback((values, action) => {
    const {
      locationId: locId,
      contactName,
      title,
      contactEmail,
      phone,
      contactType,
      guaranteeConfirm,
      guaranteeCancel,
      returns,
      vehNotCovered,
    } = values;

    const locationContact = {
      locationId: locId,
      contactName,
      title,
      contactEmail,
      phone,
      contactType,
      guaranteeConfirm,
      guaranteeCancel,
      returns,
      vehNotCovered,
    };

    if (action === AuctionLocationContactAction.Update) {
      locationContact.contactId = values.contactId;
    }

    return { locationContact };
  }, []);

  const onExecuteMutationLocationContact = useCallback(async(mutation, mutationResultName, values, action) => {
    try {
      const input = getContactActionInput(values, action);
      const response = await mutation({ variables: { ...input } });
      const { success, errors, msg } = response.data[mutationResultName];
      if (success) {
        setShowContactModal(false);
        setModalError('');
        setIsLoading(false);
        setSuccessMessage(true);

        setFlashMessage(messageSuccess[action]);
        setIsNegativeFlashMsg(false);

        setPageAction({});
        data.refetch();
      } else {
        onFailedSubmit(errors, msg);
      }
    } catch (e) {
      onFailedSubmit(e, undefined);
    }
  }, [onFailedSubmit, data, setPageAction, getContactActionInput]);

  const onFormSubmitHandler = useCallback(async values => {
    try {
      setIsLoading(true);
      setModalError('');
      const { action } = values;
      switch (action) {
        case AuctionLocationContactAction.Create:
        case AuctionLocationContactAction.Update:
          await onExecuteMutationLocationContact(createOrUpdateLocationContact, 'createOrUpdateLocationContact', values, action);
          break;
        case AuctionLocationContactAction.Delete:
          break;
        default:
          break;
      }
    } catch (error) {
      onFailedSubmit(error, undefined);
    }
  }, [createOrUpdateLocationContact, onExecuteMutationLocationContact, onFailedSubmit]);

  const onCloseContactModalHandler = () => {
    setShowContactModal(false);
    setSelectedContact(null);
    setModalError('');
    setSuccessMessage(false);
  };
  return (
    <div>
      <div className='d-flex-end'>
        <Button
          basic
          icon='file excel'
          content={isExporting ? 'Exporting...' : 'Export to CSV'}
          onClick={handleExportCSV}
          disabled={isExporting}
        />
        { csvReady && (
          <CSVLink
            data={csvData}
            headers={auctionContactHeader}
            filename={exportFileName}
            enclosingCharacter='"'
            ref={csvLinkRef}
            style={{ display: 'none' }}
          />
        )}
      </div>
      { flashMessage && (
        <Message
          onDismiss={() => {
            setFlashMessage('');
            setSuccessMessage(false);
          }}
          content={flashMessage}
          positive={!isNegativeFlashMsg}
          negative={isNegativeFlashMsg}
        />
      )}
      {contactLoading && (
        <Dimmer inverted active>
          <Loader indeterminate>Loading...</Loader>
        </Dimmer>
      )}
      <LocationContactList
        auctionLocationList={locationContactData}
        onSelectAuctionLocation={onSelectLocationContact}
        isAuctionLocationEditor={isAuctionLocationEditor}
      />
      <AuctionContactForm
        isModalOpen={showContactModal}
        title={`${selectedContact ? 'Edit' : 'Create'} Auction Contact`}
        isLoading={isLoading}
        onSubmitHandler={onFormSubmitHandler}
        onCloseHandler={onCloseContactModalHandler}
        onCloseError={() => {}}
        contactData={selectedContact}
        modelError={modalError}
        successMessage={successMessage}
        locationId={locationId}
      />
    </div>
  );
};

AllAuctionContact.propTypes = {
  user: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({
    apmsLocationContactsList: PropTypes.arrayOf(PropTypes.shape({})),
    refetch: PropTypes.func,
  }).isRequired,
  createOrUpdateLocationContact: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  setPage: () => {
    dispatch(setPage());
  },
});

export default compose(
  withRouter,
  withPagination(apmsLocationContactsQuery, 'apmsLocationContactsList'),
  LoadingDataEnhancer,
  graphql(createOrUpdateLocationContactMutation, {
    name: 'createOrUpdateLocationContact',
  }),
  connect(null, mapDispatchToProps)
)(AllAuctionContact);
